import { useIntl } from 'react-intl';
import { AmountPicker } from '@understory-io/leaf/amount-picker';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { InfoPopover } from '~/ui/info-popover';
import { FormattedPrice } from '~/ui/formatted';
import {
  perBookingMax,
  PerBookingMaxHint,
} from '~/ui/tickets-selector/per-booking-max';
import type { TicketVariantOption } from './types';

/** Returns the next quantities map for a variant change, resetting the
 *  variant's add-ons to 0 when it drops to 0. Pure — safe for both the live
 *  (desktop) and staged (mobile) paths. */
export function applyVariantChange(
  quantities: Record<string, number>,
  variant: TicketVariantOption,
  quantity: number
): Record<string, number> {
  const next = { ...quantities, [variant.id]: quantity };
  if (quantity === 0) {
    for (const addOn of variant.addOns ?? []) next[addOn.id] = 0;
  }
  return next;
}

/** Returns the next quantities map for an add-on change. */
export function applyAddOnChange(
  quantities: Record<string, number>,
  addOnId: string,
  quantity: number
): Record<string, number> {
  return { ...quantities, [addOnId]: quantity };
}

/**
 * The variant + add-on picker rows, shared by the desktop popover and the
 * mobile drawer. Emits the full next quantities map via `onChange` so the
 * caller decides whether to commit live or stage the edit.
 */
export function PickerRows({
  variants,
  quantities,
  seatStep,
  maxSeats,
  onChange,
}: {
  variants: TicketVariantOption[];
  quantities: Record<string, number>;
  seatStep: number;
  maxSeats?: number;
  onChange: (next: Record<string, number>) => void;
}) {
  const intl = useIntl();
  const bookingMax = perBookingMax(maxSeats, variants, quantities);
  const hintMax = bookingMax.reached ? bookingMax.max : undefined;

  return (
    <Stack gap="large">
      {variants.map((variant) => {
        const labelId = `tickets-field-variant-${variant.id}`;
        const variantQty = quantities[variant.id] ?? 0;
        return (
          <Stack key={variant.id} gap="small">
            <Stack
              direction="row"
              align="center"
              justify="space-between"
              gap="medium"
            >
              <Stack direction="column" gap="micro">
                <Stack direction="row" align="center" gap="xsmall">
                  <Text id={labelId} style="body-small-bold">
                    {variant.name}
                  </Text>
                  {variant.description && (
                    <InfoPopover
                      openOnHover
                      label={intl.formatMessage(
                        {
                          defaultMessage: 'Show description for {name}',
                          description:
                            'Accessible label for the ticket variant description button',
                        },
                        { name: variant.name }
                      )}
                    >
                      {variant.description}
                    </InfoPopover>
                  )}
                </Stack>
                {variant.price !== null && (
                  <Text
                    style="body-small-regular"
                    className={css({ color: 'content.secondary' })}
                  >
                    <FormattedPrice price={variant.price} />
                  </Text>
                )}
              </Stack>
              <AmountPicker
                labelledBy={labelId}
                min={0}
                max={bookingMax.variantMax(variantQty)}
                step={seatStep}
                value={variantQty}
                onChange={(value) =>
                  onChange(applyVariantChange(quantities, variant, value ?? 0))
                }
              />
            </Stack>

            {(variant.addOns ?? []).map((addOn) => {
              const addOnLabelId = `tickets-field-addon-${addOn.id}`;
              return (
                <Stack
                  key={addOn.id}
                  direction="row"
                  align="center"
                  justify="space-between"
                  gap="medium"
                  className={css({ paddingLeft: 'medium' })}
                >
                  <Stack direction="column" gap="micro">
                    <Stack direction="row" align="center" gap="xsmall">
                      <Text id={addOnLabelId} style="body-small-regular">
                        {addOn.name}
                      </Text>
                      {addOn.description && (
                        <InfoPopover
                          openOnHover
                          label={intl.formatMessage(
                            {
                              defaultMessage: 'Show description for {name}',
                              description:
                                'Accessible label for the ticket variant description button',
                            },
                            { name: addOn.name }
                          )}
                        >
                          {addOn.description}
                        </InfoPopover>
                      )}
                    </Stack>
                    {addOn.price !== null && (
                      <Text
                        style="body-small-regular"
                        className={css({ color: 'content.secondary' })}
                      >
                        <FormattedPrice price={addOn.price} />
                      </Text>
                    )}
                  </Stack>
                  <AmountPicker
                    labelledBy={addOnLabelId}
                    min={0}
                    disabled={variantQty === 0}
                    value={quantities[addOn.id] ?? 0}
                    onChange={(value) =>
                      onChange(
                        applyAddOnChange(quantities, addOn.id, value ?? 0)
                      )
                    }
                  />
                </Stack>
              );
            })}
          </Stack>
        );
      })}
      {hintMax !== undefined && <PerBookingMaxHint max={hintMax} />}
    </Stack>
  );
}
